import React, { useCallback, useMemo, useRef, useState } from 'react';

export interface ConfirmDialogRequest<A extends unknown[] = []> {
  title: string;
  message: React.ReactNode;
  onConfirm: (...args: A) => void;
  onCancel?: () => void;
}

interface DialogText {
  open: boolean;
  title: string;
  message: React.ReactNode;
}

const CLOSED: DialogText = { open: false, title: '', message: '' };

/**
 * Owns the open/title/message/confirm/cancel state for a confirmation dialog.
 *
 * `request` opens the dialog; the returned `positiveAction`/`negativeAction`
 * run the requested callback and then close it. Title and message are kept on
 * close so the text doesn't blank out during the dialog's exit transition.
 *
 * @example
 * const confirmDialog = useConfirmDialog();
 * confirmDialog.request({ title, message, onConfirm: () => dispatch(...) });
 * <ConfirmationDialog {...confirmDialog.dialogProps} />
 */
export function useConfirmDialog<A extends unknown[] = []>() {
  const [text, setText] = useState<DialogText>(CLOSED);
  const callbacks =
    useRef<Pick<ConfirmDialogRequest<A>, 'onConfirm' | 'onCancel'>>();

  const request = useCallback(
    ({ title, message, onConfirm, onCancel }: ConfirmDialogRequest<A>) => {
      callbacks.current = { onConfirm, onCancel };
      setText({ open: true, title, message });
    },
    [],
  );

  const close = useCallback(() => {
    setText((prev) => ({ ...prev, open: false }));
  }, []);

  const positiveAction = useCallback(
    (...args: A) => {
      callbacks.current?.onConfirm(...args);
      close();
    },
    [close],
  );

  const negativeAction = useCallback(() => {
    callbacks.current?.onCancel?.();
    close();
  }, [close]);

  const dialogProps = useMemo(
    () => ({ ...text, positiveAction, negativeAction }),
    [text, positiveAction, negativeAction],
  );

  return { request, close, dialogProps };
}

export interface ActivateDialogRequest extends ConfirmDialogRequest<
  [start?: string, end?: string]
> {
  message: string;
  forActivate: boolean;
}

/**
 * `useConfirmDialog` shaped for `ActivateDialog`, whose confirm receives the
 * chosen effective start/end dates.
 */
export function useActivateDialog() {
  const {
    request: requestConfirm,
    close,
    dialogProps,
  } = useConfirmDialog<[start?: string, end?: string]>();
  const [forActivate, setForActivate] = useState(true);

  const request = useCallback(
    ({ forActivate, ...rest }: ActivateDialogRequest) => {
      setForActivate(forActivate);
      requestConfirm(rest);
    },
    [requestConfirm],
  );

  const activateDialogProps = useMemo(
    () => ({
      open: dialogProps.open,
      title: dialogProps.title,
      message: dialogProps.message as string,
      forActivate,
      handleOk: dialogProps.positiveAction,
      handleCancel: dialogProps.negativeAction,
    }),
    [dialogProps, forActivate],
  );

  return { request, close, activateDialogProps };
}
